import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';

export interface BreakdownTable<T> {
  title: string;
  columns: { header: string; cell: (row: T) => ReactNode; className?: string }[];
  rows: T[];
}

// One breakdown panel rendered through the shared DataGrid so it matches every other
// table in the app (striped rows, entries selector, column search, full-numbers paging).
function BreakdownGrid<T>({ table }: { table: BreakdownTable<T> }) {
  const columns = useMemo<DataGridColumn<T>[]>(
    () =>
      table.columns.map((c, ci) => ({
        key: `c${ci}`,
        title: c.header,
        className: c.className,
        cell: c.cell,
        // Breakdown cells render a plain string/number; surface it for sort + search.
        value: (row: T) => {
          const v = c.cell(row);
          return typeof v === 'string' || typeof v === 'number' ? v : undefined;
        },
      })),
    [table.columns],
  );

  return (
    <>
      <h6 className="mb-2">{table.title}</h6>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(row) => String(table.rows.indexOf(row))}
        columnSearch
        columnVisibilityToggle={false}
        paginated={false}
        emptyMessage="No data available"
      />
    </>
  );
}

// A small card holding one or two labelled tables (port of _BreakdownCard.cshtml).
export function BreakdownCard({ tables }: { tables: BreakdownTable<unknown>[] }) {
  return (
    <div className="card custom-card mt-2">
      <div className="card-body">
        <div className="row g-3">
          {tables.map((t, i) => (
            <div className="col-md-6" key={i}>
              <BreakdownGrid table={t} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper to build a right-aligned "Total" cell renderer.
export function totalCell(v: number): ReactNode {
  return <span>{formatNumber(v, 2)}</span>;
}
